import type { PostsPage } from '@/types/posts'
import { MIX_MAX_AGE_MS } from '@/lib/mixedFeedSampling'

/**
 * Pure bounded-supply logic for the feeds mixed into the Following timeline —
 * which feeds to fetch on each round, and how each feed's accumulated page is
 * updated. Replaces the old 5-feed cap as the thing that bounds fetch cost:
 * cost scales with how far the user scrolls, not with how many feeds they
 * selected. Mirrors mobile/react-native/lib/hooks/mixedFeedSupply.ts (same
 * contract, same tests); a change here needs the same change there.
 *
 * Ported from bluesky-social/social-app @ cfd6d1e (2026-08-27) —
 * `src/lib/api/feed/merge.ts` (`MergeFeedAPI`): a few feeds per round in
 * rotation, each topped up by a small page only while it is running low, with
 * a short cutoff so a slow feed never holds a round up. Deviations: the window
 * cursor wraps with `>=` (not upstream's `>`), so a round never lands on an
 * empty window; and a feed is treated as exhausted once a whole page is older
 * than the sampler's age cutoff, since nothing past it could ever be shown.
 */

/** Feeds considered per round. Bluesky: `customFeeds.slice(feedCursor, feedCursor + 3)`. */
export const MIX_FEEDS_PER_ROUND = 3

/** Page size for each per-feed top-up. Bluesky: `feed.fetchNext(10)`. */
export const MIX_TOPUP_LIMIT = 10

/** A feed is only topped up while it has fewer than this many eligible posts queued.
 * Bluesky: `feed.numReady < 5`. */
export const MIX_LOW_WATER = 5

/** How long a round waits for its fetches before moving on. Bluesky: `REQUEST_WAIT_MS`.
 * The requests are not cancelled — a late page still lands for the next render. */
export const MIX_ROUND_WAIT_MS = 500

/** A feed's supply older than this is dropped and refetched when Following refreshes. */
export const MIX_SUPPLY_STALE_MS = 60_000

/**
 * One mixed-in feed's accumulated supply, stored as its React Query cache entry.
 * Deliberately stays `PostsPage`-shaped so the optimistic-engagement cache
 * updates (usePostEngagement.ts's `['customFeed']` prefix write) keep working
 * on it unchanged.
 */
export type MixedFeedSupply = PostsPage & {
    /** False once the feed has no further cursor, repeated a cursor, or failed. */
    hasMore: boolean
    /** When this supply was last written — drives isSupplyStale. */
    fetchedAt: number
}

export interface MixRoundState {
    /** Eligible posts still queued per feed after the latest sampling pass. */
    readyByUri: ReadonlyMap<string, number>
    /** Each feed's `hasMore` — absent means the feed has never been fetched. */
    hasMoreByUri: ReadonlyMap<string, boolean>
    /** Feeds with a request currently outstanding. */
    inFlight: ReadonlySet<string>
}

export interface MixRoundPlan {
    toFetch: string[]
    nextFeedCursor: number
}

/**
 * Plan one round: take the next MIX_FEEDS_PER_ROUND feeds from `feedCursor`
 * in `selectedUris` order, minus any that are already in flight, exhausted
 * (`hasMore === false`), or already holding at least MIX_LOW_WATER eligible
 * posts. The cursor advances by one window and wraps to 0 once it reaches the
 * end of the selection. A cursor left past the end by a shrunken selection
 * restarts at 0.
 */
export function planMixRound(
    selectedUris: readonly string[],
    feedCursor: number,
    { readyByUri, hasMoreByUri, inFlight }: MixRoundState
): MixRoundPlan {
    if (selectedUris.length === 0) return { toFetch: [], nextFeedCursor: 0 }
    const start = feedCursor >= 0 && feedCursor < selectedUris.length ? feedCursor : 0
    const end = start + MIX_FEEDS_PER_ROUND
    const window = selectedUris.slice(start, end)
    const nextFeedCursor = end >= selectedUris.length ? 0 : end
    const toFetch = window.filter(
        uri =>
            !inFlight.has(uri) &&
            hasMoreByUri.get(uri) !== false &&
            (readyByUri.get(uri) ?? 0) < MIX_LOW_WATER
    )
    return { toFetch, nextFeedCursor }
}

/**
 * Append a freshly fetched page to a feed's supply. Posts already present
 * (by uri) are skipped. `hasMore` is false when the page has no cursor,
 * returned the same cursor it was asked from (a one-step loop guard), or has
 * posts but none newer than MIX_MAX_AGE_MS — deeper pages can only be older,
 * and paging through them would add nothing the sampler could show.
 */
export function appendSupplyPage(
    prev: MixedFeedSupply | undefined,
    page: PostsPage,
    now: number
): MixedFeedSupply {
    const existing = prev?.posts ?? []
    const seen = new Set(existing.map(p => p.uri))
    const added = page.posts.filter(p => {
        if (seen.has(p.uri)) return false
        seen.add(p.uri)
        return true
    })
    const pageAgedOut =
        page.posts.length > 0 &&
        !page.posts.some(p => now - new Date(p.createdAt).getTime() <= MIX_MAX_AGE_MS)
    return {
        posts: added.length > 0 ? [...existing, ...added] : existing,
        cursor: page.cursor,
        hasMore: !!page.cursor && page.cursor !== prev?.cursor && !pageAgedOut,
        fetchedAt: now,
    }
}

/** Mark a feed's supply as exhausted after a failed fetch, keeping what it already had. */
export function markSupplyFailed(prev: MixedFeedSupply | undefined, now: number): MixedFeedSupply {
    return { ...(prev ?? { posts: [] }), hasMore: false, fetchedAt: now }
}

export function isSupplyStale(supply: Pick<MixedFeedSupply, 'fetchedAt'>, now: number): boolean {
    return now - supply.fetchedAt > MIX_SUPPLY_STALE_MS
}
